import React from 'react';
import { LayoutChangeEvent, StyleSheet } from 'react-native';

import OddsDown from '@/assets/icons/odds-down';
import OddsUp from '@/assets/icons/odds-up';
import { LineSeparator } from '@/components/LineSeparator';
import { Odds } from '@/components/Odds';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { BetSlipMarketName } from '@/feature/betslip-sbk/components/BetSlipMarketName';
import { OptionName } from '@/feature/betslip-sbk/components/OptionName';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { formatEventTeams } from '@/feature/betslip-sbk/utils/formatter';
import { useShallow } from 'zustand/react/shallow';

export const ROW_HEIGHT = 80;

type OddsChangeItemSingleProps = {
    selectionId: string;
    columnWidths: { oldOdds: number; newOdds: number };
    createLayoutHandler: (column: 'oldOdds' | 'newOdds') => (event: LayoutChangeEvent) => void;
};

const styles = StyleSheet.create({
    marketLineSeparator: {
        height: 16,
    },
});

export const OddsChangeItemSingle = ({ selectionId, columnWidths, createLayoutHandler }: OddsChangeItemSingleProps) => {
    const { optionId, eventId, marketId, eventTeams, currentOdds, oldOdds } = useSbkBetSlipStore(
        useShallow(state => {
            const selection = state.selections[selectionId];
            const option = state.options[selectionId];
            const event = state.events[selection?.eventId];

            return {
                optionId: selection?.optionId,
                eventId: selection?.eventId,
                marketId: selection?.marketId,
                eventTeams: event ? formatEventTeams(event) : '',
                currentOdds: option?.odds ?? 0,
                oldOdds: state.oddsChanges[selectionId] ?? 0,
            };
        })
    );

    if (!optionId || !eventId || !marketId) {
        return null;
    }

    const oddsIncreased = currentOdds > oldOdds;

    return (
        <Box height={80}>
            <Row py="s16" alignItems="center" justifyContent="space-between">
                <Box flex={1} pr="s16" gap="s4">
                    <Text variant="bodySmall" color="neutral300" numberOfLines={1}>
                        {eventTeams}
                    </Text>
                    <Row flexShrink={1} gap="s8" alignItems="center">
                        <OptionName
                            optionId={optionId}
                            variant="titleMedium"
                            color={ds.colorToken.text.primary}
                            numberOfLines={1}
                        />
                        <LineSeparator vertical style={styles.marketLineSeparator} />
                        <Box flexShrink={1}>
                            <BetSlipMarketName
                                eventId={eventId}
                                marketId={marketId}
                                variant="bodyMedium"
                                color="neutral300"
                                numberOfLines={1}
                            />
                        </Box>
                    </Row>
                </Box>
                <Box minWidth={columnWidths.oldOdds} pr="s16">
                    <Odds
                        odds={oldOdds}
                        variant="bodyMedium"
                        color={ds.colorToken.text.primary}
                        textAlign="right"
                        onLayout={createLayoutHandler('oldOdds')}
                    />
                </Box>
                <Row minWidth={columnWidths.newOdds} alignItems="center" justifyContent="flex-end" gap="s4">
                    {oddsIncreased ? <OddsUp /> : <OddsDown />}
                    <Odds
                        odds={currentOdds}
                        variant="bodyMedium"
                        color={oddsIncreased ? ds.colorToken.text.success : ds.colorToken.text.danger}
                        onLayout={createLayoutHandler('newOdds')}
                    />
                </Row>
            </Row>
            <LineSeparator />
        </Box>
    );
};
